// src/components/circles/SmartRulesSummary.tsx
//
// A Smart Circle's rules in plain language — people and companies by name,
// never by id. A company the viewer cannot see reads "a company you can’t
// view" (the companies list is record-scoped server-side).

import { useMemo } from 'react';
import { useGetCompaniesQuery } from '../../api/companiesApi';
import type { Circle } from '../../types/api';
import { useCircleOwnerCandidates } from './useCircleOwnerCandidates';
import { describeRule } from './smartRules';

export default function SmartRulesSummary({ circle }: { circle: Circle }) {
  const rules = circle.smartRules?.all ?? [];
  const members = useCircleOwnerCandidates(rules.some((r) => r.field === 'ownerUserId') ? circle.workspaceId : undefined);
  const needsCompanies = rules.some((r) => r.field === 'companyId');
  const { data: companiesData } = useGetCompaniesQuery({ limit: 200 }, { skip: !needsCompanies });

  const lookups = useMemo(
    () => ({
      memberName: (id: string) => {
        const m = members.members.find((x) => x.id === id);
        return m ? m.fullName?.trim() || m.email?.trim() || undefined : undefined;
      },
      companyName: (id: string) => companiesData?.companies.find((c) => c.id === id)?.name,
    }),
    [members.members, companiesData],
  );

  if (rules.length === 0) {
    return (
      <p className="text-sm text-amber-800" data-testid="smart-rules-inactive">
        No rules yet — add rules to activate. Until then this Smart Circle has no members.
      </p>
    );
  }

  return (
    <div data-testid="smart-rules-summary">
      <p className="text-sm text-gray-700">Contacts that match all of these:</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-gray-900">
        {rules.map((r, i) => (
          <li key={i} className="break-words">
            {describeRule(r, lookups)}
          </li>
        ))}
      </ul>
    </div>
  );
}
